import React from 'react';
import { flow, map, join, set, get } from 'lodash/fp';
import { Table, Icon, IconButton, Tooltip, Whisper } from 'rsuite';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import * as PERMISSIONS from '../../../../constants/permissions/inventory';
import * as RESERVATION_PERMISSIONS from '../../../../constants/permissions/reservation';
import { viewAccess, modifyAccess } from '../../../../helpers/permission';
import AccessControl from '../../../AccessControl';
import IconLink from '../../../IconLink';
import {
  ItemType,
  PackageType
} from '../../../../constants/inventory/InventoryTypes';
import InventoryActions from '../../../../constants/inventory/InventoryActions';
import IconByAdjustAction from '../../../../constants/inventory/IconByAdjustAction';
import createItemPathFor from '../../../../helpers/createItemPathFor';
import InventoryImage from '../../Image';
import Link from '../../../Link';
import { InventoryAvailabilityType } from '../../../../interfaces/inventory';

const { Cell } = Table;
const { ADD, REMOVE } = InventoryActions;
const permissionByType = {
  [ItemType]: PERMISSIONS.ITEM,
  [PackageType]: PERMISSIONS.PACKAGE
};

const ImageWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: #f5f5f5;
  border-radius: 20px;
  margin-top: -10px;
  overflow: hidden;
  display: inline-block;
`;

const QtyCellContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const QtyValue = styled.span`
  display: inline-block;
  text-align: right;
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  margin-left: 4px;
  flex-shrink: 0;
  position: relative;
  top: -3px;
`;

export const ActionCell = ({ type, rowData, dataKey, ...props }) => {
  const item = set('itemType', type, rowData.item);
  const permission = get(type, permissionByType);
  const locationQuery =
    ItemType === type
      ? { zoneId: get('zone.id', rowData) }
      : { locationId: get('location.id', rowData) };

  return (
    <Cell {...props} className="link-group">
      <AccessControl allowOnlyFor={modifyAccess(permission)}>
        {ItemType === type && (
          <IconLink
            icon={IconByAdjustAction[ADD]}
            to={createItemPathFor('adjust', item, {
              ...locationQuery,
              action: ADD
            })}
          />
        )}
        <IconLink
          icon={IconByAdjustAction[REMOVE]}
          to={createItemPathFor('adjust', item, {
            ...locationQuery,
            action: REMOVE
          })}
          disabled={rowData.quantity < 1}
        />
      </AccessControl>
      <AccessControl allowOnlyFor={viewAccess(permission)}>
        <IconLink icon="eye" to={createItemPathFor('view', item)} />
      </AccessControl>
    </Cell>
  );
};

ActionCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  type: PropTypes.number.isRequired,
  rowData: InventoryAvailabilityType
};

ActionCell.defaultProps = {
  rowData: null
};

export const TagsCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {flow(
      map('name'),
      join(', ')
    )(rowData[dataKey])}
  </Cell>
);

TagsCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: InventoryAvailabilityType
};

TagsCell.defaultProps = {
  rowData: null
};

export const LinkCell = ({ rowData, dataKey, type, ...props }) => {
  const item = set('itemType', type, rowData.item);
  return (
    <Cell {...props} className="link-group">
      <Link to={createItemPathFor('view', item)}>{get(dataKey, rowData)}</Link>
    </Cell>
  );
};

LinkCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  type: PropTypes.number.isRequired,
  rowData: InventoryAvailabilityType
};

LinkCell.defaultProps = {
  rowData: null
};

export const ImageLinkCell = ({ rowData, dataKey, type, ...props }) => {
  const item = set('itemType', type, rowData.item);
  return (
    <Cell {...props} style={{ padding: 0 }}>
      <Link to={createItemPathFor('view', item)}>
        <ImageWrapper>
          <InventoryImage image={get(dataKey, rowData)} style={{ width: 40 }} />
        </ImageWrapper>
      </Link>
    </Cell>
  );
};

ImageLinkCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  type: PropTypes.number.isRequired,
  rowData: InventoryAvailabilityType
};

ImageLinkCell.defaultProps = {
  rowData: null
};

export const OnHoldQtyCell = ({
  rowData,
  dataKey,
  onView,
  style,
  ...props
}) => {
  const quantity = get(dataKey, rowData) || 0;
  const canView = quantity > 0;

  return (
    <Cell {...props} style={{ ...style, paddingRight: 16 }}>
      <QtyCellContent>
        <QtyValue>{quantity}</QtyValue>
        <IconSlot>
          {canView && (
            <AccessControl
              allowOnlyFor={viewAccess(RESERVATION_PERMISSIONS.MODULE)}
            >
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip>Reservations with onhold items</Tooltip>}
              >
                <IconButton
                  appearance="link"
                  size="xs"
                  icon={<Icon icon="list" />}
                  onClick={() => onView(rowData)}
                  style={{ padding: 0, top: 0, height: 20, width: 20 }}
                />
              </Whisper>
            </AccessControl>
          )}
        </IconSlot>
      </QtyCellContent>
    </Cell>
  );
};

OnHoldQtyCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  onView: PropTypes.func.isRequired,
  rowData: InventoryAvailabilityType,
  style: PropTypes.shape({})
};

OnHoldQtyCell.defaultProps = {
  rowData: null,
  style: {}
};
